import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  useQuery,
  useInfiniteQuery,
  useQueries,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Image } from '@tiptap/extension-image'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import {
  Sparkles,
  Play,
  X,
  Loader2,
  Eye,
  FileText,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  XCircle,
  ListChecks,
  RefreshCw,
  History,
  ChevronDown,
  ChevronUp,
  Users,
  User,
  Upload,
  ExternalLink,
  Pencil,
  Save,
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Undo,
  Redo,
  Maximize2,
  Minimize2,
  Tag,
  Download,
  Clock,
} from 'lucide-react'
import { useUser } from '@/contexts/UserContext'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { NativeSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { enhancementsApi } from '@/services/api/enhancements'
import { evaluationsApi } from '@/services/api/evaluations'
import { servicenowApi } from '@/services/api/servicenow'
import type {
  EvaluationResult,
  EnhancementResult,
  EnhancementResultsResponse,
  JobStatusResponse,
} from '@/types/api'
import { cn } from '@/lib/utils'
import { AIBubbleMenu } from '@/components/editor/AIBubbleMenu'
import { IssueViewDialog } from '@/components/issues'

// LocalStorage keys for persisting job state
const ACTIVE_JOBS_KEY = 'enhancements_active_jobs' // JSON: { displayNumber: jobId }
const COMPLETED_JOB_KEY = 'enhancements_completed_job'

// Mirrors USER_PROMPT_MAX_CHARS on EnhancementJobRequest / SingleEnhancementRequest
// (api/schemas/requests.py), so the limit is met at the keystroke, not as a 422.
const USER_PROMPT_MAX = 2000

// Time-window steps for "Recently Enhanced" mode. "See more" paginates within the
// current window first, then steps to the next wider window when exhausted. Each
// wider window is a superset of the narrower one, so its pages replace the list.
const RECENTLY_ENHANCED_WINDOWS: (number | null)[] = [30, 90, null]
type RecentPageParam = { windowIdx: number; page: number }

// One row per article across pages. A page boundary can shift between requests
// when an audit or enhancement lands, so the same article may arrive twice.
function dedupeByDisplayNumber<T extends { display_number: string }>(items: T[]): T[] {
  const seen = new Set<string>()
  return items.filter((item) => {
    if (seen.has(item.display_number)) return false
    seen.add(item.display_number)
    return true
  })
}

// Issue parsing helper
function parseIssues(issueText: string | undefined): { severity: string; text: string }[] {
  if (!issueText) return []

  const trimmedText = issueText.trim()

  // Check for "no issues" messages - return empty array
  if (
    trimmedText.toLowerCase().includes('no issues found') ||
    trimmedText.toLowerCase().includes('no issues identified') ||
    trimmedText.toLowerCase() === 'none' ||
    trimmedText === '-' ||
    trimmedText === ''
  ) {
    return []
  }

  const issues: { severity: string; text: string }[] = []

  // Split by numbered issue pattern (1., 2., etc. followed by [HIGH/MEDIUM/LOW)
  // This regex splits on patterns like "1. [HIGH" or "2. [MEDIUM" while keeping the delimiter
  const issuePattern = /(?=\d+\.\s*\[(?:HIGH|MEDIUM|LOW))/gi
  const issueBlocks = trimmedText.split(issuePattern).filter(block => block.trim())

  for (const block of issueBlocks) {
    const trimmedBlock = block.trim()
    if (!trimmedBlock) continue

    // Determine severity from the block
    const highMatch = trimmedBlock.match(/\[HIGH/i)
    const medMatch = trimmedBlock.match(/\[MEDIUM/i)
    const lowMatch = trimmedBlock.match(/\[LOW/i)

    if (highMatch) {
      issues.push({ severity: 'high', text: trimmedBlock })
    } else if (medMatch) {
      issues.push({ severity: 'medium', text: trimmedBlock })
    } else if (lowMatch) {
      issues.push({ severity: 'low', text: trimmedBlock })
    }
    // Don't add as 'info' - only add actual issues with severity tags
  }

  return issues
}

// Rich Text Editor Toolbar Button
function ToolbarButton({
  onClick,
  isActive,
  disabled,
  children,
  title,
}: {
  onClick: () => void
  isActive?: boolean
  disabled?: boolean
  children: React.ReactNode
  title: string
}) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={cn(
              'p-1.5 rounded hover:bg-muted transition-colors',
              isActive && 'bg-muted text-primary',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs">{title}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// Editor styles that match the article preview
const editorStyles = `
  .tiptap-editor {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #333;
    padding: 16px;
    min-height: 300px;
  }

  .tiptap-editor:focus {
    outline: none;
  }

  .tiptap-editor p {
    margin: 0 0 1em 0;
  }

  .tiptap-editor h1 {
    font-size: 1.75em;
    font-weight: 600;
    margin: 1em 0 0.5em 0;
    line-height: 1.3;
  }

  .tiptap-editor h2 {
    font-size: 1.5em;
    font-weight: 600;
    margin: 1em 0 0.5em 0;
    line-height: 1.3;
  }

  .tiptap-editor h3 {
    font-size: 1.25em;
    font-weight: 600;
    margin: 1em 0 0.5em 0;
    line-height: 1.3;
  }

  .tiptap-editor h4, .tiptap-editor h5, .tiptap-editor h6 {
    font-size: 1.1em;
    font-weight: 600;
    margin: 1em 0 0.5em 0;
    line-height: 1.3;
  }

  .tiptap-editor ul, .tiptap-editor ol {
    margin: 0.5em 0 1em 0;
    padding-left: 1.5em;
  }

  .tiptap-editor ul {
    list-style-type: disc;
  }

  .tiptap-editor ol {
    list-style-type: decimal;
  }

  .tiptap-editor li {
    margin: 0.25em 0;
  }

  .tiptap-editor li p {
    margin: 0;
  }

  .tiptap-editor strong, .tiptap-editor b {
    font-weight: 600;
  }

  .tiptap-editor em, .tiptap-editor i {
    font-style: italic;
  }

  .tiptap-editor a {
    color: #0066cc;
    text-decoration: underline;
  }

  .tiptap-editor blockquote {
    border-left: 3px solid #ddd;
    margin: 1em 0;
    padding-left: 1em;
    color: #666;
  }

  .tiptap-editor pre {
    background: #f5f5f5;
    border-radius: 4px;
    padding: 0.75em 1em;
    margin: 1em 0;
    overflow-x: auto;
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 0.9em;
  }

  .tiptap-editor code {
    background: #f5f5f5;
    border-radius: 3px;
    padding: 0.2em 0.4em;
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 0.9em;
  }

  .tiptap-editor pre code {
    background: none;
    padding: 0;
  }

  .tiptap-editor img {
    max-width: 100%;
    height: auto;
    margin: 1em 0;
  }

  .tiptap-editor table {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
  }

  .tiptap-editor th, .tiptap-editor td {
    border: 1px solid #ddd;
    padding: 8px 12px;
    text-align: left;
    vertical-align: top;
  }

  .tiptap-editor th {
    background: #f5f5f5;
    font-weight: 600;
  }

  .tiptap-editor tr:nth-child(even) {
    background: #fafafa;
  }

  .tiptap-editor hr {
    border: none;
    border-top: 1px solid #ddd;
    margin: 1.5em 0;
  }

  /* Selection styling */
  .tiptap-editor .ProseMirror-selectednode {
    outline: 2px solid #0066cc;
  }

  /* Keep text selection visible even when editor loses focus */
  .tiptap-editor ::selection {
    background: #b4d5fe;
  }

  .tiptap-editor:not(:focus-within) ::selection {
    background: #b4d5fe;
  }

  /* Ensure selection stays visible */
  .tiptap-editor .ProseMirror {
    caret-color: #333;
  }
`

// Rich Text Editor Component
function RichTextEditor({
  content,
  onChange,
  className,
  articleContext,
}: {
  content: string
  onChange: (html: string) => void
  className?: string
  articleContext?: string
}) {
  const [isFullscreen, setIsFullscreen] = useState(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Image,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
    },
  })

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isFullscreen])

  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isFullscreen])

  if (!editor) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'border rounded-lg bg-white relative',
        isFullscreen && 'fixed inset-0 z-50 rounded-none flex flex-col',
        !isFullscreen && className
      )}
    >
      {/* Inject editor styles */}
      <style>{editorStyles}</style>

      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b bg-muted/30 flex-wrap sticky top-0 z-10">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <Undo className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo className="h-4 w-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-border mx-1" />
        {/* Fullscreen Toggle */}
        <ToolbarButton
          onClick={() => setIsFullscreen(!isFullscreen)}
          isActive={isFullscreen}
          title={isFullscreen ? "Exit Fullscreen (Esc)" : "Fullscreen"}
        >
          {isFullscreen ? (
            <Minimize2 className="h-4 w-4" />
          ) : (
            <Maximize2 className="h-4 w-4" />
          )}
        </ToolbarButton>
        {/* AI Enhancement indicator */}
        <div className="flex-1" />
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          Select text for AI
        </div>
      </div>

      {/* Editor Content */}
      <div className={cn(
        "overflow-auto relative",
        isFullscreen ? "flex-1" : "max-h-[calc(50vh-60px)]"
      )}>
        <EditorContent editor={editor} />
        {/* AI Bubble Menu - shows when text is selected */}
        {articleContext && (
          <AIBubbleMenu
            editor={editor}
            articleContext={articleContext}
          />
        )}
      </div>
    </div>
  )
}

// Side-by-Side Enhancement Result Preview with ServiceNow Publish
function EnhancementResultPreview({
  result,
  open,
  onClose,
}: {
  result: EnhancementResult | null
  open: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  // State for editing
  const [isEditing, setIsEditing] = useState(false)
  const [editedHtml, setEditedHtml] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [contentSaved, setContentSaved] = useState(false) // Track if edited content was saved

  // State for title editing
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState<string | null>(null)
  const [titleSaveStatus, setTitleSaveStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  // State for publish operation
  const [publishStatus, setPublishStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [publishError, setPublishError] = useState<string | null>(null)
  // Set when the backend rejects publish with 409 ARTICLE_NOT_PUBLISHED —
  // article is no longer "published" in ServiceNow (e.g. outdated/retired).
  const [publishBlockedState, setPublishBlockedState] = useState<string | null>(null)

  // State for re-submission confirmation dialog
  const [showResubmitConfirm, setShowResubmitConfirm] = useState(false)

  // State for details drawer
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false)

  // State for issue view dialog (with verification results)
  const [issueViewDialogOpen, setIssueViewDialogOpen] = useState(false)

  // State for manual actions confirmation checkbox
  const [manualActionsConfirmed, setManualActionsConfirmed] = useState(false)

  // Reset state when modal opens/closes or result changes
  useEffect(() => {
    if (!open) {
      setPublishStatus('idle')
      setPublishError(null)
      setPublishBlockedState(null)
      setIsEditing(false)
      setEditedHtml(null)
      setSaveStatus('idle')
      setContentSaved(false)
      setShowResubmitConfirm(false)
      setDetailsDrawerOpen(false)
      setIssueViewDialogOpen(false)
      setManualActionsConfirmed(false)
      setIsEditingTitle(false)
      setEditedTitle(null)
      setTitleSaveStatus('idle')
    }
  }, [open, result?.display_number])

  // Track if content has been modified (but not yet saved)
  const hasChanges = editedHtml !== null && editedHtml !== result?.enhanced_html && !contentSaved

  // Current displayed title (falls back through edited → enhanced → original)
  const currentTitle = editedTitle ?? result?.enhanced_title ?? result?.article_title ?? ''
  const savedTitle = result?.enhanced_title ?? result?.article_title ?? ''
  const hasTitleChanges = editedTitle !== null && editedTitle.trim() !== savedTitle

  // Handle editor content change
  const handleEditorChange = useCallback((html: string) => {
    setEditedHtml(html)
    setContentSaved(false) // Reset saved state when user makes new changes
  }, [])

  // Save title mutation (standalone — for the inline Save button)
  const handleSaveTitle = async () => {
    if (!result || editedTitle === null) return
    const trimmed = editedTitle.trim()
    if (!trimmed || trimmed === savedTitle) {
      setIsEditingTitle(false)
      setEditedTitle(null)
      return
    }
    try {
      setTitleSaveStatus('loading')
      await enhancementsApi.updateTitle(result.display_number, trimmed)
      setTitleSaveStatus('success')
      setIsEditingTitle(false)
      setEditedTitle(null)
      queryClient.invalidateQueries({ queryKey: ['enhancements'] })
    } catch (error) {
      console.error('Failed to save title:', error)
      setTitleSaveStatus('error')
    }
  }

  const handleCancelTitleEdit = () => {
    setIsEditingTitle(false)
    setEditedTitle(null)
    setTitleSaveStatus('idle')
  }

  // Save and Publish to ServiceNow mutation
  const publishMutation = useMutation({
    mutationFn: async ({
      displayNumber,
      htmlToSave,
      titleToSave,
    }: { displayNumber: string; htmlToSave?: string; titleToSave?: string }) => {
      // If title was edited, save it first
      if (titleToSave) {
        await enhancementsApi.updateTitle(displayNumber, titleToSave)
      }
      // If content was edited, save it next
      if (htmlToSave) {
        setSaveStatus('loading')
        await enhancementsApi.updateContent(displayNumber, htmlToSave)
        setSaveStatus('success')
      }
      // Publish to ServiceNow
      const publishResult = await servicenowApi.publishByDisplayNumber(displayNumber)
      // Mark as submitted in our database
      await enhancementsApi.markAsSubmitted(displayNumber)
      // Invalidate cache to refresh the result with new submission status
      queryClient.invalidateQueries({ queryKey: ['enhancements'] })
      return publishResult
    },
    onMutate: () => {
      setPublishStatus('loading')
      setPublishError(null)
      setPublishBlockedState(null)
    },
    onSuccess: () => {
      setPublishStatus('success')
      setIsEditing(false)
      setContentSaved(true) // Mark content as saved so "Unsaved changes" badge disappears
      setIsEditingTitle(false)
      setEditedTitle(null)
      // Don't clear editedHtml - keep showing the saved content until modal closes
    },
    onError: (error: Error) => {
      setPublishStatus('error')
      setSaveStatus('error')
      // Detect the 409 ARTICLE_NOT_PUBLISHED guard from the backend so we can
      // show a tailored warning (article expired/retired in SN since evaluation).
      // FastAPI wraps HTTPException(detail=…) bodies as {"detail": …}, so the
      // backend error payload lives at data.detail.error (not data.error).
      type BackendError = {
        code?: string
        message?: string
        details?: { current_workflow_state?: string }
      }
      const axiosErr = error as Error & {
        response?: {
          status?: number
          data?: { detail?: { error?: BackendError }; error?: BackendError }
        }
      }
      const backendError =
        axiosErr.response?.data?.detail?.error ?? axiosErr.response?.data?.error
      if (backendError?.code === 'ARTICLE_NOT_PUBLISHED') {
        setPublishBlockedState(backendError.details?.current_workflow_state || 'unknown')
        setPublishError(backendError.message || 'Article is no longer published in ServiceNow.')
      } else {
        setPublishError(error.message || 'Failed to save or publish')
      }
    },
  })

  // Handle submit button click - show confirmation if already submitted
  const handleSubmitClick = () => {
    if (result?.submitted_to_servicenow) {
      setShowResubmitConfirm(true)
    } else {
      publishMutation.mutate({
        displayNumber: result!.display_number,
        htmlToSave: hasChanges ? editedHtml! : undefined,
        titleToSave: hasTitleChanges ? editedTitle!.trim() : undefined,
      })
    }
  }

  // Confirm re-submission
  const handleConfirmResubmit = () => {
    setShowResubmitConfirm(false)
    publishMutation.mutate({
      displayNumber: result!.display_number,
      htmlToSave: hasChanges ? editedHtml! : undefined,
      titleToSave: hasTitleChanges ? editedTitle!.trim() : undefined,
    })
  }

  if (!result) return null

  // Parse metatags from JSON or comma-separated string
  const parseMetatags = (metatags: string | undefined) => {
    if (!metatags) return []
    try {
      const parsed = JSON.parse(metatags)
      if (parsed.keywords) return parsed.keywords
      if (Array.isArray(parsed)) return parsed
      return []
    } catch {
      return metatags.split(',').map((t: string) => t.trim()).filter(Boolean)
    }
  }

  const metatags = parseMetatags(result.enhanced_metatags)
  const issuesRequiringAction = result.issues_requiring_action?.split(';').filter(Boolean) || []
  const hasManualActions = issuesRequiringAction.length > 0
  const canPublish = result.enhancement_status === 'completed' && result.enhanced_html && manualActionsConfirmed

  // Extract KB number from display_number (e.g., "KB0071349 v1.0" -> "KB0071349")
  const extractKbNumber = (displayNumber: string) => {
    const match = displayNumber.match(/^(KB\d+)/i)
    return match ? match[1] : displayNumber
  }

  const kbNumber = extractKbNumber(result.display_number)

  // Generate original article URL (Founda portal)
  const originalArticleUrl = `https://founda.danone.com/ecportal?id=kb_article&sysparm_article=${kbNumber}`

  // Generate published article URL (ServiceNow backend view)
  const getPublishedArticleUrl = () => {
    const baseUrl = 'https://danone.service-now.com/now/nav/ui/classic/params/target/'
    const targetPath = `kb_knowledge_list.do?sysparm_first_row=1&sysparm_query=GOTOnumber%3D${kbNumber}&sysparm_query_encoded=GOTOnumber%253d${kbNumber}&sysparm_view=Default+View`
    return `${baseUrl}${encodeURIComponent(targetPath)}`
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader>
          {isEditingTitle ? (
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                value={editedTitle ?? ''}
                onChange={(e) => setEditedTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSaveTitle()
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    handleCancelTitleEdit()
                  }
                }}
                maxLength={255}
                disabled={titleSaveStatus === 'loading'}
                className="text-lg font-semibold"
                placeholder="Article title"
              />
              <Button
                size="sm"
                onClick={handleSaveTitle}
                disabled={titleSaveStatus === 'loading' || !editedTitle?.trim() || editedTitle.trim() === savedTitle}
                className="h-8"
              >
                {titleSaveStatus === 'loading' ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Save className="h-3 w-3" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelTitleEdit}
                disabled={titleSaveStatus === 'loading'}
                className="h-8"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <DialogTitle className="text-lg">{currentTitle}</DialogTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditedTitle(savedTitle)
                  setIsEditingTitle(true)
                  setTitleSaveStatus('idle')
                }}
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                title="Edit title"
              >
                <Pencil className="h-3 w-3" />
              </Button>
              {titleSaveStatus === 'error' && (
                <span className="text-xs text-destructive">Failed to save — try again</span>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
            <span className="font-mono">{result.display_number}</span>
            <Badge variant="outline" className="text-xs">
              {result.knowledge_base}
            </Badge>
            {(result.author_name || result.ownership_group_name) && (
              <span className="text-sm">
                {[result.author_name, result.ownership_group_name].filter(Boolean).join(' · ')}
              </span>
            )}
            <Badge
              variant="outline"
              className={cn(
                'text-xs',
                result.enhancement_status === 'completed' && 'bg-green-100 text-green-800',
                result.enhancement_status === 'failed' && 'bg-red-100 text-red-800',
              )}
            >
              {result.enhancement_status}
            </Badge>
            {result.submitted_to_servicenow && (
              <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800">
                <Upload className="h-3 w-3 mr-1" />
                Submitted to ServiceNow
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* Side-by-Side HTML Preview */}
        <div className="grid grid-cols-2 gap-4 flex-1 min-h-0">
          {/* Original Article */}
          <div className="flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-2">
              <Badge variant="outline" className="bg-gray-100">Original</Badge>
              <a
                href={originalArticleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                View in Founda
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            <div className="flex-1 border rounded-lg bg-white overflow-auto">
              {result.original_raw_html ? (
                <iframe
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <meta charset="UTF-8">
                      <style>
                        body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; padding: 16px; padding-bottom: 32px; line-height: 1.6; color: #333; font-size: 14px; margin: 0; }
                        img { max-width: 100%; height: auto; }
                        table { border-collapse: collapse; width: 100%; }
                        th, td { border: 1px solid #ddd; padding: 8px; }
                      </style>
                    </head>
                    <body>${result.original_raw_html}</body>
                    </html>
                  `}
                  className="w-full h-full"
                  title="Original Article"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <FileText className="h-8 w-8 mr-2" />
                  <span>Original HTML not available</span>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Article */}
          <div className="flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-green-100 text-green-800">Enhanced</Badge>
                {hasChanges && (
                  <Badge variant="outline" className="bg-yellow-100 text-yellow-800 text-xs">
                    Unsaved changes
                  </Badge>
                )}
                {result.edited_by_email && !hasChanges && (
                  <span className="text-xs text-muted-foreground">
                    Edited by {result.edited_by_email}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasChanges && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditedHtml(null)
                      setIsEditing(false)
                    }}
                    className="h-7 text-xs text-muted-foreground hover:text-destructive"
                  >
                    <Undo className="h-3 w-3 mr-1.5" />
                    Discard Changes
                  </Button>
                )}
                <Button
                  variant={isEditing ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    if (isEditing) {
                      // Exit edit mode - keep editedHtml so Preview shows changes
                      setIsEditing(false)
                    } else {
                      // Enter edit mode with current content (edited or original)
                      setIsEditing(true)
                      if (editedHtml === null) {
                        setEditedHtml(result.enhanced_html || '')
                      }
                    }
                  }}
                  disabled={!result.enhanced_html}
                  className="h-7 text-xs"
                >
                  {isEditing ? (
                    <>
                      <Save className="h-3 w-3 mr-1.5" />
                      Save
                    </>
                  ) : (
                    <>
                      <Pencil className="h-3 w-3 mr-1.5" />
                      Edit
                    </>
                  )}
                </Button>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              {isEditing && editedHtml !== null ? (
                <RichTextEditor
                  content={editedHtml}
                  onChange={handleEditorChange}
                  className="h-full"
                  articleContext={result.enhanced_html || ''}
                />
              ) : (editedHtml || result.enhanced_html) ? (
                <div className="border rounded-lg bg-white overflow-auto h-full">
                  <iframe
                    srcDoc={`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <meta charset="UTF-8">
                        <style>
                          body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; padding: 16px; padding-bottom: 32px; line-height: 1.6; color: #333; font-size: 14px; margin: 0; }
                          img { max-width: 100%; height: auto; }
                          table { border-collapse: collapse; width: 100%; }
                          th, td { border: 1px solid #ddd; padding: 8px; }
                        </style>
                      </head>
                      <body>${editedHtml || result.enhanced_html}</body>
                      </html>
                    `}
                    className="w-full h-full"
                    title="Enhanced Article"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground border rounded-lg bg-white">
                  <FileText className="h-8 w-8 mr-2" />
                  <span>Enhanced HTML not available</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Compact Details Summary Row */}
        {(() => {
          const improvementsList = result.improvements_summary
            ? result.improvements_summary
                .split('\n')
                .map(line => line.replace(/^[-•*]\s*/, '').trim())
                .filter(line => line.length > 0)
            : []
          const hasDetails = result.ai_summary || improvementsList.length > 0 || metatags.length > 0 || issuesRequiringAction.length > 0

          return hasDetails ? (
            <div className="mt-4 flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
              <div className="flex items-center gap-4 text-sm flex-wrap">
                {result.ai_summary && (
                  <span className="flex items-center gap-1.5 text-purple-700">
                    <Sparkles className="h-4 w-4" />
                    <span>AI Summary</span>
                  </span>
                )}
                {improvementsList.length > 0 && (
                  <span className="flex items-center gap-1.5 text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>{improvementsList.length} improvement{improvementsList.length !== 1 ? 's' : ''}</span>
                  </span>
                )}
                {metatags.length > 0 && (
                  <span className="flex items-center gap-1.5 text-blue-700">
                    <Tag className="h-4 w-4" />
                    <span>{metatags.length} metatag{metatags.length !== 1 ? 's' : ''}</span>
                  </span>
                )}
                {issuesRequiringAction.length > 0 && (
                  <span className="flex items-center gap-1.5 text-yellow-700">
                    <AlertTriangle className="h-4 w-4" />
                    <span>{issuesRequiringAction.length} issue{issuesRequiringAction.length !== 1 ? 's' : ''}</span>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIssueViewDialogOpen(true)}
                  className="shrink-0"
                >
                  <ListChecks className="h-4 w-4 mr-2" />
                  View Issues
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDetailsDrawerOpen(true)}
                  className="shrink-0"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </Button>
              </div>
            </div>
          ) : (
            // Show just the View Issues button even if no other details
            <div className="mt-4 flex items-center justify-end p-3 bg-gray-50 rounded-lg border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIssueViewDialogOpen(true)}
                className="shrink-0"
              >
                <ListChecks className="h-4 w-4 mr-2" />
                View Issues
              </Button>
            </div>
          )
        })()}

        {/* Details Side Drawer */}
        <Sheet open={detailsDrawerOpen} onOpenChange={setDetailsDrawerOpen}>
          <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Enhancement Details</SheetTitle>
              <SheetDescription>
                {result.enhanced_title || result.article_title}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {/* AI Summary */}
              {result.ai_summary && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-medium text-sm flex items-center gap-2 mb-3">
                    <Sparkles className="h-4 w-4 text-purple-600" />
                    <span className="text-purple-800">AI Summary</span>
                  </h4>
                  <p className="text-sm text-purple-900 leading-relaxed">{result.ai_summary}</p>
                </div>
              )}

              {/* Improvements Made */}
              {result.improvements_summary && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-sm flex items-center gap-2 mb-3">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-green-800">Improvements Made</span>
                  </h4>
                  <ul className="text-sm text-green-900 space-y-2">
                    {result.improvements_summary
                      .split('\n')
                      .map(line => line.replace(/^[-•*]\s*/, '').trim())
                      .filter(line => line.length > 0)
                      .map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-green-500 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {/* Enhanced Title */}
              {result.enhanced_title && result.enhanced_title !== result.article_title && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-medium text-sm flex items-center gap-2 mb-2">
                    <Pencil className="h-4 w-4 text-purple-600" />
                    <span className="text-purple-800">Enhanced Title</span>
                  </h4>
                  <div className="text-sm space-y-1">
                    <p className="text-gray-500 line-through">{result.article_title}</p>
                    <p className="text-purple-900 font-medium">{result.enhanced_title}</p>
                  </div>
                </div>
              )}

              {/* Metatags */}
              {metatags.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-sm flex items-center gap-2 mb-3">
                    <Tag className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-800">New Metatags ({metatags.length})</span>
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {metatags.map((tag: string, i: number) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Issues Requiring Action */}
              {issuesRequiringAction.length > 0 && (
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <h4 className="font-medium text-sm flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <span className="text-yellow-800">Requires Manual Action ({issuesRequiringAction.length})</span>
                  </h4>
                  <ul className="text-sm text-yellow-900 space-y-2">
                    {issuesRequiringAction.map((issue, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-yellow-500 mt-1">•</span>
                        <span>{issue.trim()}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 pt-3 border-t border-yellow-200">
                    <p className="text-xs text-yellow-700 flex items-center gap-1.5">
                      <Info className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>For editing, adding, or removing images, please use the ServiceNow portal directly.</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Footer with Enhancement Info and Publish Button */}
        <div className="mt-2 border-t pt-3 shrink-0 space-y-2">
          {/* Manual Actions Confirmation */}
          {publishStatus !== 'success' && (
            <div className="p-2.5 bg-yellow-50 border border-yellow-200 rounded-lg">
              {hasManualActions && (
                <details className="mb-1.5 group">
                  <summary className="text-xs font-medium text-yellow-800 flex items-center gap-1.5 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span>{issuesRequiringAction.length} issue{issuesRequiringAction.length !== 1 ? 's' : ''} requiring manual review</span>
                    <ChevronDown className="h-3 w-3 shrink-0 transition-transform group-open:rotate-180" />
                  </summary>
                  <ul className="text-xs text-yellow-700 mt-1.5 space-y-0.5 pl-5 list-disc max-h-24 overflow-y-auto">
                    {issuesRequiringAction.map((issue, i) => (
                      <li key={i}>{issue.trim()}</li>
                    ))}
                  </ul>
                </details>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={manualActionsConfirmed}
                  onCheckedChange={(checked) => setManualActionsConfirmed(checked === true)}
                />
                <span className="text-xs text-yellow-900">
                  I acknowledge the changes
                </span>
              </label>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              <span>Enhanced on {new Date(result.enhancement_date).toLocaleString()}</span>
              <span className="mx-2">•</span>
              <span>{result.num_iterations} iteration(s)</span>
            </div>

            {/* Publish to ServiceNow Section */}
            <div className="flex items-center gap-3">
              {/* Status Messages */}
              {publishStatus === 'success' && (
                <div className="flex items-center gap-3 text-sm text-green-600">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Submitted</span>
                  </div>
                  <span className="text-gray-500">Next Step:</span>
                  <a
                    href={getPublishedArticleUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1 underline"
                  >
                    Continue in ServiceNow
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {publishStatus === 'error' && publishBlockedState && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                  <div>
                    <div className="font-medium">
                      Article no longer published in ServiceNow
                    </div>
                    <div className="mt-0.5">
                      Current state in ServiceNow: <strong>{publishBlockedState}</strong>.
                      Submission was blocked to avoid creating a draft on a retired
                      or outdated article. Please re-evaluate this article before
                      enhancing again.
                    </div>
                  </div>
                </div>
              )}
              {publishStatus === 'error' && !publishBlockedState && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <XCircle className="h-4 w-4" />
                  <span>{publishError}</span>
                </div>
              )}

            {/* Save & Submit Button */}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleSubmitClick}
                    disabled={!canPublish || publishStatus === 'loading' || publishStatus === 'success'}
                    className={cn(
                      publishStatus === 'success' && 'bg-green-600 hover:bg-green-600'
                    )}
                  >
                    {publishStatus === 'loading' ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {saveStatus === 'loading' ? 'Saving...' : 'Publishing...'}
                      </>
                    ) : publishStatus === 'success' ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Submitted
                      </>
                    ) : hasChanges ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save & Submit
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Submit to ServiceNow
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="text-xs">
                    {hasChanges
                      ? 'Save your edits and create a draft in ServiceNow'
                      : 'Create a draft in ServiceNow'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            </div>
          </div>
        </div>

        {/* Re-submission Confirmation Dialog */}
        <Dialog open={showResubmitConfirm} onOpenChange={setShowResubmitConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                Article Already Submitted
              </DialogTitle>
              <DialogDescription className="pt-2">
                This article was previously submitted to ServiceNow
                {result.submitted_at && (
                  <span> on {new Date(result.submitted_at).toLocaleDateString()}</span>
                )}
                {result.submitted_by_email && (
                  <span> by {result.submitted_by_email}</span>
                )}
                .
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Important:</strong> Please ensure the previous draft has been deleted or published in ServiceNow before creating a new one. Submitting again will create a duplicate draft.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowResubmitConfirm(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmResubmit}>
                Submit Anyway
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Issue View Dialog with Verification Results */}
        <IssueViewDialog
          open={issueViewDialogOpen}
          onOpenChange={setIssueViewDialogOpen}
          displayNumber={result.display_number}
          articleTitle={result.enhanced_title || result.article_title}
          showVerification={true}
          onRequestFreshEvaluation={(displayNumber) => {
            setIssueViewDialogOpen(false)
            evaluationsApi.single({ display_number: displayNumber, force_fresh: true })
              .then(() => {
                queryClient.invalidateQueries({ queryKey: ['article-issues', displayNumber] })
              })
          }}
        />
      </DialogContent>
    </Dialog>
  )
}

// Article Card Component
function ArticleCard({
  article,
  selected,
  onSelect,
  onPreview,
  enhancementStatus,
}: {
  article: EvaluationResult
  selected: boolean
  onSelect: () => void
  onPreview: () => void
  enhancementStatus: 'enhancing' | 'completed' | 'failed' | null
}) {
  const flagColors: Record<string, string> = {
    'Critical change needed': 'bg-red-100 text-red-800 border-red-200',
    'Needs improvement': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'High quality': 'bg-green-100 text-green-800 border-green-200',
  }

  return (
    <Card
      className={cn(
        'hover:shadow-md transition-all cursor-pointer',
        selected && 'ring-2 ring-primary',
        enhancementStatus === 'enhancing' && 'ring-2 ring-blue-400/50',
        enhancementStatus === 'completed' && 'ring-2 ring-green-400/50',
        enhancementStatus === 'failed' && 'ring-2 ring-red-400/50',
      )}
      onClick={onPreview}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="pt-1" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected}
              onCheckedChange={onSelect}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-mono text-xs text-muted-foreground">
                {article.display_number}
              </span>
              <Badge
                variant="outline"
                className={cn('text-xs', flagColors[article.flag] || '')}
              >
                {article.flag}
              </Badge>
              {enhancementStatus && (
                <Badge
                  variant="outline"
                  className={cn(
                    'text-xs flex items-center gap-1',
                    enhancementStatus === 'enhancing' && 'bg-blue-100 text-blue-800 border-blue-200',
                    enhancementStatus === 'completed' && 'bg-green-100 text-green-800 border-green-200',
                    enhancementStatus === 'failed' && 'bg-red-100 text-red-800 border-red-200',
                  )}
                >
                  {enhancementStatus === 'enhancing' && <Loader2 className="h-3 w-3 animate-spin" />}
                  {enhancementStatus === 'completed' && <CheckCircle2 className="h-3 w-3" />}
                  {enhancementStatus === 'failed' && <XCircle className="h-3 w-3" />}
                  {enhancementStatus === 'enhancing' && 'Enhancing...'}
                  {enhancementStatus === 'completed' && 'Enhanced'}
                  {enhancementStatus === 'failed' && 'Failed'}
                </Badge>
              )}
            </div>
            <h3 className="font-medium text-sm line-clamp-2 mb-2">
              {article.article_title}
            </h3>
            <div className="flex items-center gap-3 text-xs">
              <TooltipProvider delayDuration={200}>
                {article.issue_count_high > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 text-red-600 cursor-pointer">
                        <AlertTriangle className="h-3 w-3" />
                        {article.issue_count_high}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Critical Issues - Require immediate attention</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {article.issue_count_medium > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 text-yellow-600 cursor-pointer">
                        <AlertCircle className="h-3 w-3" />
                        {article.issue_count_medium}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Medium Issues - Should be addressed</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {article.issue_count_low > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 text-blue-600 cursor-pointer">
                        <Info className="h-3 w-3" />
                        {article.issue_count_low}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Low Issues - Minor improvements suggested</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </div>
            {(article.author_name || article.ownership_group_name) && (
              <p className="text-xs text-muted-foreground mt-1">
                {[article.author_name, article.ownership_group_name].filter(Boolean).join(' · ')}
              </p>
            )}
            <div className="flex flex-wrap gap-1 mt-2">
              <Badge variant="outline" className="text-xs font-normal">
                {article.knowledge_base}
              </Badge>
              <Badge variant="outline" className="text-xs font-normal">
                {article.language}
              </Badge>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation()
              onPreview()
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// Article Preview Modal with Issues Drawer
function ArticlePreviewModal({
  article,
  open,
  onClose,
  onEnhance,
  enhancementStatus,
  onViewEnhancement,
}: {
  article: EvaluationResult | null
  open: boolean
  onClose: () => void
  onEnhance?: (displayNumber: string) => void
  enhancementStatus?: 'enhancing' | 'completed' | 'failed' | null
  onViewEnhancement?: (displayNumber: string) => void
}) {
  const [issuesDrawerOpen, setIssuesDrawerOpen] = useState(false)
  const [issueViewDialogOpen, setIssueViewDialogOpen] = useState(false)

  // Fetch full article detail with raw_html. The backend answers 404 when the
  // article is no longer published, latest and in an active knowledge base —
  // possible if ServiceNow changed it after the list was loaded.
  const { data: articleDetail, isLoading, isError } = useQuery({
    queryKey: ['evaluation', 'detail', article?.display_number],
    queryFn: () => evaluationsApi.getResult(article!.display_number),
    enabled: open && !!article?.display_number,
    retry: false,
  })

  const detail = articleDetail?.result

  // Parse issues by category
  const readabilityIssues = parseIssues(detail?.readability_evaluations)
  const relevancyIssues = parseIssues(detail?.relevancy_evaluations)
  const visualIssues = parseIssues(detail?.visual_evaluations)
  const totalIssues = readabilityIssues.length + relevancyIssues.length + visualIssues.length

  if (!article) return null

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <div>
                <DialogTitle className="text-lg">{article.article_title}</DialogTitle>
                <DialogDescription className="flex items-center gap-2 mt-1">
                  <span className="font-mono">{article.display_number}</span>
                  {(article.author_name || article.ownership_group_name) && (
                    <span className="text-sm">
                      {[article.author_name, article.ownership_group_name].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  <Badge variant="outline" className="text-xs">
                    {article.knowledge_base}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {article.flag}
                  </Badge>
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIssueViewDialogOpen(true)}
                >
                  <ListChecks className="h-4 w-4 mr-2" />
                  View Issues ({article.issue_count_total})
                </Button>
                {onEnhance && (
                  <Button
                    size="sm"
                    disabled={enhancementStatus === 'enhancing'}
                    onClick={() => {
                      onEnhance(article.display_number)
                      onClose()
                    }}
                  >
                    {enhancementStatus === 'enhancing' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    {enhancementStatus === 'enhancing' ? 'Enhancing...' : 'Enhance'}
                  </Button>
                )}
              </div>
            </div>
          </DialogHeader>

          {/* Enhancement available banner */}
          {enhancementStatus === 'completed' && onViewEnhancement && (
            <div className="flex items-center justify-between px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-green-800">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>This article has been enhanced and is ready for review.</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 bg-white border-green-300 text-green-800 hover:bg-green-100"
                onClick={() => {
                  onViewEnhancement(article!.display_number)
                  onClose()
                }}
              >
                <Eye className="h-4 w-4 mr-2" />
                View Enhanced Result
              </Button>
            </div>
          )}

          {enhancementStatus === 'enhancing' && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              <span>Enhancement is in progress. You can check back shortly.</span>
            </div>
          )}

          <div className="flex-1 overflow-hidden rounded-lg border bg-white">
            {isLoading ? (
              <div className="flex items-center justify-center h-[500px]">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center h-[500px] gap-2 text-center px-8">
                <p className="text-sm font-medium">This article is no longer available.</p>
                <p className="text-sm text-muted-foreground">
                  It is not the current published version in an active knowledge base any more,
                  so it cannot be viewed or enhanced here. Refresh the list to see what is current.
                </p>
              </div>
            ) : detail?.raw_html ? (
              <iframe
                srcDoc={`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <meta charset="UTF-8">
                    <style>
                      body {
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        padding: 20px;
                        line-height: 1.6;
                        color: #333;
                      }
                      img { max-width: 100%; height: auto; }
                      table { border-collapse: collapse; width: 100%; }
                      th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                      th { background: #f5f5f5; }
                    </style>
                  </head>
                  <body>${detail.raw_html}</body>
                  </html>
                `}
                className="w-full h-[500px]"
                title="Article Preview"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-[500px] text-muted-foreground">
                <FileText className="h-12 w-12 mb-4" />
                <p>No HTML content available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Issues Side Drawer */}
      <Sheet open={issuesDrawerOpen} onOpenChange={setIssuesDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Issues Found</SheetTitle>
            <SheetDescription>
              {article.issue_count_total} issues identified in this article
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Readability Issues */}
            {readabilityIssues.length > 0 && (
              <div>
                <h4 className="font-medium text-sm flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4" />
                  Readability Issues ({readabilityIssues.length})
                </h4>
                <div className="space-y-2">
                  {readabilityIssues.map((issue, i) => (
                    <div
                      key={i}
                      className={cn(
                        'p-3 rounded-lg text-sm',
                        issue.severity === 'high' && 'bg-red-50 border border-red-200',
                        issue.severity === 'medium' && 'bg-yellow-50 border border-yellow-200',
                        issue.severity === 'low' && 'bg-blue-50 border border-blue-200',
                        issue.severity === 'info' && 'bg-gray-50 border border-gray-200',
                      )}
                    >
                      {issue.text}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Relevancy Issues */}
            {relevancyIssues.length > 0 && (
              <div>
                <h4 className="font-medium text-sm flex items-center gap-2 mb-3">
                  <AlertCircle className="h-4 w-4" />
                  Relevancy Issues ({relevancyIssues.length})
                </h4>
                <div className="space-y-2">
                  {relevancyIssues.map((issue, i) => (
                    <div
                      key={i}
                      className={cn(
                        'p-3 rounded-lg text-sm',
                        issue.severity === 'high' && 'bg-red-50 border border-red-200',
                        issue.severity === 'medium' && 'bg-yellow-50 border border-yellow-200',
                        issue.severity === 'low' && 'bg-blue-50 border border-blue-200',
                        issue.severity === 'info' && 'bg-gray-50 border border-gray-200',
                      )}
                    >
                      {issue.text}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Visual Issues */}
            {visualIssues.length > 0 && (
              <div>
                <h4 className="font-medium text-sm flex items-center gap-2 mb-3">
                  <Eye className="h-4 w-4" />
                  Visual Issues ({visualIssues.length})
                </h4>
                <div className="space-y-2">
                  {visualIssues.map((issue, i) => (
                    <div
                      key={i}
                      className={cn(
                        'p-3 rounded-lg text-sm',
                        issue.severity === 'high' && 'bg-red-50 border border-red-200',
                        issue.severity === 'medium' && 'bg-yellow-50 border border-yellow-200',
                        issue.severity === 'low' && 'bg-blue-50 border border-blue-200',
                        issue.severity === 'info' && 'bg-gray-50 border border-gray-200',
                      )}
                    >
                      {issue.text}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {totalIssues === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p>No issues found</p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Structured Issue View Dialog with Feedback */}
      {article && (
        <IssueViewDialog
          open={issueViewDialogOpen}
          onOpenChange={setIssueViewDialogOpen}
          displayNumber={article.display_number}
          articleTitle={article.article_title}
          showVerification={false}
        />
      )}
    </>
  )
}

// Active Job Card
function ActiveJobCard({
  job,
  onCancel,
}: {
  job: JobStatusResponse
  onCancel: () => void
}) {
  const progress = job.progress.percentage

  return (
    <Card className="border-primary/50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium">{job.job_name || job.job_id}</span>
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <Progress value={progress} className="mb-2" />
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>
            {job.progress.processed_articles} of {job.progress.total_articles} articles
          </span>
          <span>{progress.toFixed(0)}%</span>
        </div>
      </CardContent>
    </Card>
  )
}

// Completed Job Card
function CompletedJobCard({
  job,
  onDismiss,
  onViewResults,
}: {
  job: JobStatusResponse
  onDismiss: () => void
  onViewResults: () => void
}) {
  const isSuccess = job.status === 'completed'
  const isFailed = job.status === 'failed'

  return (
    <Card className={cn(
      'border-2',
      isSuccess && 'border-green-500/50 bg-green-50/50',
      isFailed && 'border-red-500/50 bg-red-50/50',
      !isSuccess && !isFailed && 'border-yellow-500/50 bg-yellow-50/50'
    )}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isSuccess ? (
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            ) : isFailed ? (
              <XCircle className="h-6 w-6 text-red-600" />
            ) : (
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            )}
            <div>
              <p className="font-medium">{job.job_name || 'Enhancement Job'}</p>
              <p className="text-sm text-muted-foreground">
                {isSuccess && `${job.progress.successful_articles} articles enhanced successfully`}
                {isFailed && (job.error_message || 'Job failed')}
                {!isSuccess && !isFailed && 'Job was cancelled'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSuccess && job.progress.successful_articles > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={onViewResults}
                className="bg-white"
              >
                <Eye className="h-4 w-4 mr-2" />
                View Results
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onDismiss}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Main Page Component
export function EnhancementsPage() {
  const queryClient = useQueryClient()
  const { user } = useUser()

  // Check if user is admin (can see all jobs/articles)
  const isAdmin = user?.role === 'Admin' || user?.role === 'SuperUser'

  // Get user's permitted KBs (for normal users)
  const userPermittedKBs = user?.permissions || []

  // State
  const [flagFilter, setFlagFilter] = useState<string>('Critical change needed')
  const [knowledgeBase, setKnowledgeBase] = useState('')
  const [authorFilter, setAuthorFilter] = useState('')
  const [ownershipGroupFilter, setOwnershipGroupFilter] = useState('')
  const [searchDisplayNumber, setSearchDisplayNumber] = useState('')
  const [searchResults, setSearchResults] = useState<EvaluationResult[] | null>(null) // null = not searching
  const [isSearching, setIsSearching] = useState(false)
  const [userPrompt, setUserPrompt] = useState('')
  const [jobName, setJobName] = useState('')
  const [selectedArticles, setSelectedArticles] = useState<Set<string>>(new Set())
  const [previewArticle, setPreviewArticle] = useState<EvaluationResult | null>(null)
  const [enhancementResultPreview, setEnhancementResultPreview] = useState<EnhancementResult | null>(null)
  const [historyExpanded, setHistoryExpanded] = useState(false)
  const [showMyEnhancementsOnly, setShowMyEnhancementsOnly] = useState(false) // Toggle for enhanced articles filter
  const [enhancementsDisplayNumberFilter, setEnhancementsDisplayNumberFilter] = useState('') // Display number filter for enhanced articles
  const [isExportingIssues, setIsExportingIssues] = useState(false) // Export issues loading state
  const [viewingJobResults, setViewingJobResults] = useState<string | null>(null) // Job ID to view results in modal

  // View mode: switch the main grid between articles needing enhancement
  // and recently enhanced articles (KB-scoped to user permissions on the backend).
  const [viewMode, setViewMode] = useState<'needs-enhancement' | 'recently-enhanced'>('needs-enhancement')

  // Debounced server-side search by display_number / kb_number
  useEffect(() => {
    const trimmed = searchDisplayNumber.trim()
    if (trimmed.length < 3) {
      setSearchResults(null)
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    const timer = setTimeout(async () => {
      try {
        const response = await evaluationsApi.searchResults(trimmed)
        setSearchResults(response.results || [])
      } catch (err) {
        console.error('Search failed:', err)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 400)

    return () => clearTimeout(timer)
  }, [searchDisplayNumber])

  // Multi-job tracking: displayNumber -> jobId (persisted to localStorage)
  const [activeJobMap, setActiveJobMap] = useState<Record<string, string>>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(ACTIVE_JOBS_KEY)
      if (stored) {
        try { return JSON.parse(stored) } catch { return {} }
      }
    }
    return {}
  })
  // Recently completed article statuses for card badges: displayNumber -> status
  const [completedArticles, setCompletedArticles] = useState<Record<string, 'completed' | 'failed'>>({})
  // Completed batch job for top-level notification card
  const [completedBatchJob, setCompletedBatchJob] = useState<JobStatusResponse | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(COMPLETED_JOB_KEY)
      if (stored) {
        try { return JSON.parse(stored) } catch { return null }
      }
    }
    return null
  })
  // Track which jobs we've already processed as completed (to avoid infinite loops)
  const processedJobsRef = useRef<Set<string>>(new Set())

  // Set default KB for normal users with only one permitted KB
  useEffect(() => {
    if (!isAdmin && userPermittedKBs.length === 1 && !knowledgeBase) {
      setKnowledgeBase(userPermittedKBs[0])
    }
  }, [isAdmin, userPermittedKBs, knowledgeBase])

  // Persist state to localStorage
  useEffect(() => {
    if (Object.keys(activeJobMap).length > 0) {
      localStorage.setItem(ACTIVE_JOBS_KEY, JSON.stringify(activeJobMap))
    } else {
      localStorage.removeItem(ACTIVE_JOBS_KEY)
    }
  }, [activeJobMap])

  useEffect(() => {
    if (completedBatchJob) {
      localStorage.setItem(COMPLETED_JOB_KEY, JSON.stringify(completedBatchJob))
    } else {
      localStorage.removeItem(COMPLETED_JOB_KEY)
    }
  }, [completedBatchJob])

  // Fetch filters
  const { data: filters } = useQuery({
    queryKey: ['evaluations', 'filters'],
    queryFn: () => evaluationsApi.getFilters(),
  })

  // Fetch evaluation results (articles with issues). Pages accumulate inside the
  // query, keyed by the filters: changing a filter starts again from page 1, and
  // leaving the filters as they are keeps the pages already loaded. There is no
  // separate accumulator to clear - clearing one while the query key stayed the
  // same is what made Reset show an empty list when the filters were already at
  // their defaults.
  const {
    data: evalResults,
    isLoading: resultsLoading,
    isFetching,
    hasNextPage: hasMoreResults,
    fetchNextPage: fetchMoreResults,
    isFetchingNextPage: isLoadingMoreResults,
  } = useInfiniteQuery({
    queryKey: ['evaluations', 'results', flagFilter, knowledgeBase, authorFilter, ownershipGroupFilter],
    queryFn: ({ pageParam }) =>
      evaluationsApi.listResults({
        flag: flagFilter,
        knowledge_base: knowledgeBase || undefined,
        author_name: authorFilter || undefined,
        ownership_group_name: ownershipGroupFilter || undefined,
        page: pageParam,
        limit: 100,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.pages ? lastPage.pagination.page + 1 : undefined,
    staleTime: 30000,
  })

  const allResults = useMemo(
    () => dedupeByDisplayNumber(evalResults?.pages.flatMap((page) => page.results) ?? []),
    [evalResults],
  )
  const resultsTotal = evalResults?.pages[0]?.pagination.total ?? 0

  // Derive unique active job IDs from the map
  const activeJobIds = useMemo(() => [...new Set(Object.values(activeJobMap))], [activeJobMap])

  // Poll all active jobs (1.5s intervals, stops when finished)
  const jobQueries = useQueries({
    queries: activeJobIds.map(jobId => ({
      queryKey: ['enhancements', 'job', jobId],
      queryFn: () => enhancementsApi.getJobStatus(jobId),
      refetchInterval: (query: { state: { data: JobStatusResponse | undefined } }) => {
        const data = query.state.data
        if (!data) return 1500
        if (['completed', 'failed', 'cancelled'].includes(data.status)) return false
        return 1500
      },
    })),
  })

  // Build lookup: jobId -> JobStatusResponse
  const jobStatusMap = useMemo(() => {
    const map: Record<string, JobStatusResponse> = {}
    jobQueries.forEach((q, i) => {
      if (q.data) map[activeJobIds[i]] = q.data
    })
    return map
  }, [jobQueries, activeJobIds])

  // Always fetch recent enhancement results for card status badges (survives refresh)
  const { data: recentEnhancements } = useQuery({
    queryKey: ['enhancements', 'recent-status'],
    queryFn: () => enhancementsApi.listResults({ limit: 200 }),
    staleTime: 30000,
  })

  // Build lookup: displayNumber -> EnhancementResult
  const enhancedArticlesMap = useMemo(() => {
    const map: Record<string, EnhancementResult> = {}
    if (recentEnhancements?.results) {
      for (const result of recentEnhancements.results) {
        map[result.display_number] = result
      }
    }
    return map
  }, [recentEnhancements])

  // Get enhancement status for a specific article
  const getArticleStatus = useCallback((displayNumber: string): 'enhancing' | 'completed' | 'failed' | null => {
    // 1. Check recently completed (in-session)
    if (completedArticles[displayNumber]) return completedArticles[displayNumber]
    // 2. Check actively enhancing jobs
    const jobId = activeJobMap[displayNumber]
    if (jobId) {
      const jobStatus = jobStatusMap[jobId]
      if (!jobStatus) return 'enhancing'
      if (jobStatus.status === 'completed') return 'completed'
      if (jobStatus.status === 'failed' || jobStatus.status === 'cancelled') return 'failed'
      return 'enhancing'
    }
    // 3. Fallback: check backend enhancement results (survives refresh)
    const enhResult = enhancedArticlesMap[displayNumber]
    if (enhResult) {
      if (enhResult.enhancement_status === 'completed') return 'completed'
      if (enhResult.enhancement_status === 'failed') return 'failed'
    }
    return null
  }, [activeJobMap, jobStatusMap, completedArticles, enhancedArticlesMap])

  // Find active batch jobs (multi-article) for top-level progress cards
  const activeBatchJobs = useMemo(() => {
    return Object.values(jobStatusMap)
      .filter(status => !['completed', 'failed', 'cancelled'].includes(status.status))
      .filter(status => status.progress.total_articles > 1)
  }, [jobStatusMap])

  // Handle job completion: move finished jobs from activeJobMap to completedArticles
  useEffect(() => {
    const newlyFinished: { jobId: string; status: JobStatusResponse }[] = []

    for (const [jobId, status] of Object.entries(jobStatusMap)) {
      if (['completed', 'failed', 'cancelled'].includes(status.status) && !processedJobsRef.current.has(jobId)) {
        processedJobsRef.current.add(jobId)
        newlyFinished.push({ jobId, status })
      }
    }

    if (newlyFinished.length === 0) return

    const finishedJobIds = new Set(newlyFinished.map(j => j.jobId))
    const articlesToComplete: Record<string, 'completed' | 'failed'> = {}

    setActiveJobMap(prev => {
      const next = { ...prev }
      for (const [dn, jid] of Object.entries(next)) {
        if (finishedJobIds.has(jid)) {
          const job = newlyFinished.find(j => j.jobId === jid)!
          articlesToComplete[dn] = job.status.status === 'completed' ? 'completed' : 'failed'
          delete next[dn]
        }
      }
      return next
    })

    setCompletedArticles(prev => ({ ...prev, ...articlesToComplete }))

    // Show batch completion card for multi-article jobs
    for (const { status } of newlyFinished) {
      if (status.progress.total_articles > 1) {
        setCompletedBatchJob(status)
      }
    }

    queryClient.invalidateQueries({ queryKey: ['enhancements'] })
  }, [jobStatusMap, queryClient])

  // Fetch enhancement history (jobs)
  const { data: jobsHistory, isLoading: jobsLoading } = useQuery({
    queryKey: ['enhancements', 'jobs', 'history'],
    queryFn: () => enhancementsApi.listJobs({ limit: 20 }),
    enabled: historyExpanded,
  })

  // Fetch enhancement results (all recent articles or user's articles)
  const { data: enhancementHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['enhancements', 'history', showMyEnhancementsOnly],
    queryFn: () => enhancementsApi.listResults({ limit: 20, my_results_only: showMyEnhancementsOnly }),
    enabled: historyExpanded,
  })

  // Fetch results for a specific job (when viewing job results modal)
  const { data: jobResultsData, isLoading: jobResultsLoading } = useQuery({
    queryKey: ['enhancements', 'job-results', viewingJobResults],
    queryFn: () => enhancementsApi.listResults({ job_id: viewingJobResults!, limit: 100 }),
    enabled: !!viewingJobResults,
  })

  // Fetch recently-enhanced articles for the "Recently Enhanced" view mode.
  // Backend already KB-scopes to the caller's permissions; we just forward
  // the optional KB narrow filter and time-window. "See more" advances within
  // the current window first and, once it is exhausted, steps to the next
  // wider window (30 -> 90 -> all time). Pages are keyed by (window, page) and
  // accumulate inside the query, so a KB change, a mode switch or Reset never
  // has to clear anything by hand.
  const {
    data: recentResults,
    isLoading: recentLoading,
    isFetching: recentFetching,
    hasNextPage: canSeeMoreRecent,
    fetchNextPage: handleSeeMoreRecent,
    isFetchingNextPage: isLoadingMoreRecent,
  } = useInfiniteQuery<
    EnhancementResultsResponse,
    Error,
    InfiniteData<EnhancementResultsResponse, RecentPageParam>,
    QueryKey,
    RecentPageParam
  >({
    queryKey: ['enhancements', 'recently-enhanced', knowledgeBase],
    queryFn: ({ pageParam }) =>
      enhancementsApi.listResults({
        knowledge_base: knowledgeBase || undefined,
        since_days: RECENTLY_ENHANCED_WINDOWS[pageParam.windowIdx] ?? undefined,
        page: pageParam.page,
        limit: 50,
      }),
    initialPageParam: { windowIdx: 0, page: 1 },
    getNextPageParam: (lastPage, _allPages, lastParam) => {
      if (lastPage.pagination.page < lastPage.pagination.pages) {
        return { windowIdx: lastParam.windowIdx, page: lastParam.page + 1 }
      }
      if (lastParam.windowIdx < RECENTLY_ENHANCED_WINDOWS.length - 1) {
        return { windowIdx: lastParam.windowIdx + 1, page: 1 }
      }
      return undefined
    },
    enabled: viewMode === 'recently-enhanced',
    staleTime: 30000,
  })

  // The widest window fetched so far is a superset of the narrower ones, so
  // only its pages are shown; the narrower pages held the same articles.
  const recentWindowIdx = recentResults
    ? Math.max(...recentResults.pageParams.map((param) => param.windowIdx))
    : 0
  const recentSinceDays = RECENTLY_ENHANCED_WINDOWS[recentWindowIdx]
  const recentAccumulated = useMemo(() => {
    if (!recentResults) return []
    const pagesInWindow = recentResults.pages.filter(
      (_, index) => recentResults.pageParams[index].windowIdx === recentWindowIdx,
    )
    return dedupeByDisplayNumber(pagesInWindow.flatMap((page) => page.results))
  }, [recentResults, recentWindowIdx])

  const recentLastPage = recentResults?.pages[recentResults.pages.length - 1]
  const recentTotal = recentLastPage?.pagination.total ?? 0
  const recentHasMoreInWindow = recentLastPage
    ? recentLastPage.pagination.page < recentLastPage.pagination.pages
    : false
  const recentNextWindow = RECENTLY_ENHANCED_WINDOWS[recentWindowIdx + 1]
  const seeMoreLabel = recentHasMoreInWindow
    ? `See more (${recentAccumulated.length} of ${recentTotal})`
    : recentWindowIdx < RECENTLY_ENHANCED_WINDOWS.length - 1
      ? recentNextWindow === null
        ? 'See all-time enhanced articles'
        : `See enhanced articles in last ${recentNextWindow} days`
      : ''

  // Submit batch job mutation
  const submitJob = useMutation({
    mutationFn: () =>
      enhancementsApi.submit({
        article_ids: selectedArticles.size > 0 ? Array.from(selectedArticles) : undefined,
        source_flag_filter: selectedArticles.size === 0 ? (flagFilter as 'Critical change needed' | 'Needs improvement' | 'all') : undefined,
        knowledge_base: knowledgeBase || undefined,
        user_prompt: userPrompt || undefined,
        job_name: jobName || undefined,
      }),
    onSuccess: (data) => {
      // Track all articles in this batch job
      const articles = selectedArticles.size > 0
        ? Array.from(selectedArticles)
        : filteredResults.map(r => r.display_number)
      setActiveJobMap(prev => {
        const next = { ...prev }
        articles.forEach(dn => { next[dn] = data.job_id })
        return next
      })
      setSelectedArticles(new Set())
      setJobName('')
    },
  })

  // Cancel job mutation
  const cancelJob = useMutation({
    mutationFn: (jobId: string) => enhancementsApi.cancelJob(jobId),
    onSuccess: (_, jobId) => {
      setActiveJobMap(prev => {
        const next = { ...prev }
        for (const [dn, jid] of Object.entries(next)) {
          if (jid === jobId) delete next[dn]
        }
        return next
      })
      processedJobsRef.current.add(jobId)
      queryClient.invalidateQueries({ queryKey: ['enhancements'] })
    },
  })

  // Quick enhance single article mutation
  const quickEnhance = useMutation({
    mutationFn: (displayNumber: string) =>
      enhancementsApi.submit({
        article_ids: [displayNumber],
        knowledge_base: knowledgeBase || undefined,
        user_prompt: userPrompt || undefined,
        job_name: `Quick enhance: ${displayNumber}`,
      }),
    onSuccess: (data, displayNumber) => {
      setActiveJobMap(prev => ({ ...prev, [displayNumber]: data.job_id }))
    },
  })

  // Handler for quick enhance from preview modal
  const handleQuickEnhance = (displayNumber: string) => {
    if (activeJobMap[displayNumber]) return // Already enhancing
    quickEnhance.mutate(displayNumber)
  }

  // Handler for viewing enhancement result from preview modal
  const handleViewEnhancement = async (displayNumber: string) => {
    const detail = await enhancementsApi.getResult(displayNumber)
    setEnhancementResultPreview(detail.result)
  }

  // Use server search results when searching, otherwise show all paginated results
  const filteredResults = searchResults !== null ? searchResults : allResults

  const articlesCount = filteredResults.length

  // Toggle article selection
  const toggleArticle = (displayNumber: string) => {
    const newSelected = new Set(selectedArticles)
    if (newSelected.has(displayNumber)) {
      newSelected.delete(displayNumber)
    } else {
      newSelected.add(displayNumber)
    }
    setSelectedArticles(newSelected)
  }

  // Select all visible articles
  const selectAll = () => {
    if (selectedArticles.size === filteredResults.length) {
      setSelectedArticles(new Set())
    } else {
      setSelectedArticles(new Set(filteredResults.map(r => r.display_number)))
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-secondary/30">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Enhance Articles</h1>
            <p className="text-muted-foreground">
              Review issues and enhance articles with AI
            </p>
          </div>
        </div>
      </div>

      {/* Active Batch Jobs */}
      <AnimatePresence>
        {activeBatchJobs.map(job => (
          <motion.div
            key={job.job_id}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <ActiveJobCard
              job={job}
              onCancel={() => cancelJob.mutate(job.job_id)}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Completed Batch Job */}
      <AnimatePresence>
        {completedBatchJob && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <CompletedJobCard
              job={completedBatchJob}
              onDismiss={() => setCompletedBatchJob(null)}
              onViewResults={() => {
                setHistoryExpanded(true)
                setTimeout(() => {
                  document.getElementById('enhancement-history')?.scrollIntoView({ behavior: 'smooth' })
                  queryClient.invalidateQueries({ queryKey: ['enhancements', 'history'] })
                }, 100)
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Mode Toggle */}
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="shrink-0">View</Label>
            <div className="flex rounded-lg border overflow-hidden">
              <button
                className={cn(
                  'px-4 py-1.5 text-sm font-medium flex items-center gap-1.5 transition-colors',
                  viewMode === 'needs-enhancement'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-muted'
                )}
                onClick={() => setViewMode('needs-enhancement')}
              >
                <AlertCircle className="h-3.5 w-3.5" />
                Articles Needing Enhancement
              </button>
              <button
                className={cn(
                  'px-4 py-1.5 text-sm font-medium flex items-center gap-1.5 transition-colors',
                  viewMode === 'recently-enhanced'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-muted'
                )}
                onClick={() => {
                  setViewMode('recently-enhanced')
                  setSelectedArticles(new Set())
                }}
              >
                <Clock className="h-3.5 w-3.5" />
                Recently Enhanced
              </button>
            </div>
            {viewMode === 'recently-enhanced' && (
              <span className="text-xs text-muted-foreground">
                {recentSinceDays === null
                  ? 'Showing all enhanced articles you can access'
                  : `Showing articles enhanced in the last ${recentSinceDays} days`}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            {viewMode === 'needs-enhancement' && (
            <div className="flex-1 min-w-[200px]">
              <Label className="mb-1 block">Issue Severity</Label>
              <NativeSelect
                value={flagFilter}
                onChange={(e) => {
                  setFlagFilter(e.target.value)
                  setSelectedArticles(new Set())
                }}
                options={[
                  { value: 'Critical change needed', label: 'Critical change needed' },
                  { value: 'Needs improvement', label: 'Needs improvement' },
                  { value: 'High quality', label: 'High quality' },
                  { value: 'all', label: 'All evaluated articles' },
                ]}
              />
            </div>
            )}
            <div className="flex-1 min-w-[200px]">
              <Label className="mb-1 block">Knowledge Base</Label>
              <NativeSelect
                value={knowledgeBase}
                onChange={(e) => {
                  setKnowledgeBase(e.target.value)
                  setSelectedArticles(new Set())
                }}
                options={[
                  // For normal users, only show "All" if they have multiple KBs
                  ...(isAdmin || userPermittedKBs.length > 1
                    ? [{ value: '', label: isAdmin ? 'All Knowledge Bases' : 'All My Knowledge Bases' }]
                    : []),
                  // Filter KB options: admins see all, normal users see only their permitted KBs
                  ...(isAdmin
                    ? (filters?.knowledge_bases || [])
                    : (filters?.knowledge_bases || []).filter((kb) => userPermittedKBs.includes(kb))
                  ).map((kb) => ({
                    value: kb,
                    label: kb,
                  })),
                ]}
              />
            </div>
            {viewMode === 'needs-enhancement' && (
            <div className="flex-1 min-w-[200px]">
              <Label className="mb-1 block">Author</Label>
              <NativeSelect
                value={authorFilter}
                onChange={(e) => {
                  setAuthorFilter(e.target.value)
                  setSelectedArticles(new Set())
                }}
                options={[
                  { value: '', label: 'All Authors' },
                  ...(filters?.authors || []).map((a) => ({
                    value: a,
                    label: a,
                  })),
                ]}
              />
            </div>
            )}
            {viewMode === 'needs-enhancement' && (
            <div className="flex-1 min-w-[200px]">
              <Label className="mb-1 block">Ownership Group</Label>
              <NativeSelect
                value={ownershipGroupFilter}
                onChange={(e) => {
                  setOwnershipGroupFilter(e.target.value)
                  setSelectedArticles(new Set())
                }}
                options={[
                  { value: '', label: 'All Ownership Groups' },
                  ...(filters?.ownership_groups || []).map((g) => ({
                    value: g,
                    label: g,
                  })),
                ]}
              />
            </div>
            )}
            {viewMode === 'needs-enhancement' && (
            <div className="flex-1 min-w-[200px]">
              <Label className="mb-1 block">Search by KB Number</Label>
              <Input
                placeholder="e.g. KB0056252"
                value={searchDisplayNumber}
                onChange={(e) => setSearchDisplayNumber(e.target.value)}
              />
            </div>
            )}
            <Button
              variant="outline"
              onClick={() => {
                // Restores the defaults. The lists are keyed by these filters, so
                // whatever is already loaded for the defaults stays on screen.
                setFlagFilter('Critical change needed')
                setKnowledgeBase('')
                setAuthorFilter('')
                setOwnershipGroupFilter('')
                setSearchDisplayNumber('')
                setSearchResults(null)
                setSelectedArticles(new Set())
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            {viewMode === 'needs-enhancement' && (
            <Button
              variant="outline"
              disabled={isExportingIssues}
              onClick={async () => {
                setIsExportingIssues(true)
                try {
                  await enhancementsApi.exportBulkIssues({
                    flag: flagFilter,
                    knowledge_base: knowledgeBase || undefined,
                    author_name: authorFilter || undefined,
                    ownership_group_name: ownershipGroupFilter || undefined,
                  })
                } catch (err) {
                  console.error('Failed to export issues:', err)
                } finally {
                  setIsExportingIssues(false)
                }
              }}
            >
              {isExportingIssues ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export Issues
            </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {viewMode === 'needs-enhancement' && (
        <>
          {/* Additional Instructions */}
          <Card>
            <CardContent className="p-4">
              <Label className="mb-2 block">Additional Instructions (Optional)</Label>
              <Textarea
                placeholder="E.g., Focus on improving clarity, add more examples, ensure consistent terminology..."
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                rows={2}
                maxLength={USER_PROMPT_MAX}
              />
            </CardContent>
          </Card>

          {/* Action Bar */}
          <div className="flex items-center justify-between py-3 px-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-4">
              <Checkbox
                checked={selectedArticles.size === articlesCount && articlesCount > 0}
                onCheckedChange={selectAll}
              />
              <span className="text-sm font-medium">
                {articlesCount} of {resultsTotal || articlesCount} articles
                {isFetching && !isLoadingMoreResults && <Loader2 className="inline h-3 w-3 ml-2 animate-spin" />}
              </span>
              {selectedArticles.size > 0 && (
                <Badge variant="secondary">
                  {selectedArticles.size} selected
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Input
                placeholder="Job name (optional)"
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
                className="w-48 h-9"
              />
              <Button
                onClick={() => submitJob.mutate()}
                disabled={submitJob.isPending || activeBatchJobs.length > 0 || articlesCount === 0}
                size="sm"
              >
                {submitJob.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                {selectedArticles.size > 0
                  ? `Enhance ${selectedArticles.size} Selected`
                  : `Enhance All (${articlesCount})`}
              </Button>
            </div>
          </div>

          {/* Article Cards Grid */}
          {resultsLoading || isSearching ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(isSearching ? 3 : 6)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-5 w-full mb-2" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredResults.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p className="text-lg font-medium">
                  {searchDisplayNumber ? 'No articles match your search' : 'No articles need enhancement'}
                </p>
                <p className="text-muted-foreground">
                  {searchDisplayNumber
                    ? `No results found for "${searchDisplayNumber}"`
                    : 'All articles matching your filters are in good shape'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredResults.map((article) => (
                  <ArticleCard
                    key={article.display_number}
                    article={article}
                    selected={selectedArticles.has(article.display_number)}
                    onSelect={() => toggleArticle(article.display_number)}
                    onPreview={() => setPreviewArticle(article)}
                    enhancementStatus={getArticleStatus(article.display_number)}
                  />
                ))}
              </div>
              {/* Load More Button */}
              {hasMoreResults && searchResults === null && (
                <div className="flex justify-center py-4">
                  <Button
                    variant="outline"
                    onClick={() => fetchMoreResults()}
                    disabled={isLoadingMoreResults}
                  >
                    {isLoadingMoreResults ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : null}
                    {isLoadingMoreResults
                      ? 'Loading...'
                      : `Load More (${allResults.length} of ${resultsTotal})`}
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {viewMode === 'recently-enhanced' && (
        <>
          {/* Count strip */}
          <div className="flex items-center justify-between py-3 px-4 bg-muted/30 rounded-lg">
            <span className="text-sm font-medium">
              {recentAccumulated.length} of {recentTotal || recentAccumulated.length} articles
              {recentFetching && !isLoadingMoreRecent && <Loader2 className="inline h-3 w-3 ml-2 animate-spin" />}
            </span>
          </div>

          {/* Recently Enhanced Cards Grid */}
          {recentLoading && recentAccumulated.length === 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-5 w-full mb-2" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : recentAccumulated.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg font-medium">No recently enhanced articles</p>
                <p className="text-muted-foreground">
                  {recentSinceDays === null
                    ? 'No enhanced articles match your access.'
                    : `No articles enhanced in the last ${recentSinceDays} days for your knowledge bases.`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {recentAccumulated.map((result) => (
                  <Card
                    key={result.display_number}
                    className="cursor-pointer transition-colors hover:bg-muted/30"
                    onClick={async () => {
                      const detail = await enhancementsApi.getResult(result.display_number)
                      setEnhancementResultPreview(detail.result)
                    }}
                  >
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-muted-foreground">
                          {result.display_number}
                        </span>
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-xs',
                            result.enhancement_status === 'completed' && 'bg-green-100 text-green-800',
                            result.enhancement_status === 'failed' && 'bg-red-100 text-red-800',
                          )}
                        >
                          {result.enhancement_status}
                        </Badge>
                        {result.submitted_to_servicenow && (
                          <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800">
                            <Upload className="h-3 w-3 mr-1" />
                            Submitted
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-medium line-clamp-2">{result.article_title}</p>
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        <p className="truncate">{result.knowledge_base}</p>
                        <p>
                          Enhanced {result.enhancement_date ? new Date(result.enhancement_date).toLocaleDateString() : '—'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {canSeeMoreRecent && (
                <div className="flex justify-center py-4">
                  <Button
                    variant="outline"
                    onClick={() => handleSeeMoreRecent()}
                    disabled={isLoadingMoreRecent}
                  >
                    {isLoadingMoreRecent ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : null}
                    {isLoadingMoreRecent ? 'Loading...' : seeMoreLabel}
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Enhancement History Section */}
      <Card id="enhancement-history" className="mt-6">
        <CardHeader
          className="cursor-pointer"
          onClick={() => setHistoryExpanded(!historyExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Enhancement History</CardTitle>
            </div>
            <Button variant="ghost" size="sm">
              {historyExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
          <CardDescription>View past enhancement jobs and results</CardDescription>
        </CardHeader>
        <AnimatePresence>
          {historyExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <CardContent>
                {/* Jobs List */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Recent Jobs
                  </h4>
                  {jobsLoading ? (
                    <div className="space-y-2">
                      {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className="h-14 w-full" />
                      ))}
                    </div>
                  ) : jobsHistory?.jobs.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4 text-sm">
                      No enhancement jobs yet
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {jobsHistory?.jobs.map((job) => (
                        <div
                          key={job.job_id}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => setViewingJobResults(job.job_id)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">{job.job_name || job.job_id}</span>
                              <Badge
                                variant="outline"
                                className={cn(
                                  'text-xs',
                                  job.status === 'completed' && 'bg-green-100 text-green-800',
                                  job.status === 'failed' && 'bg-red-100 text-red-800',
                                  job.status === 'processing' && 'bg-blue-100 text-blue-800',
                                )}
                              >
                                {job.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span>{job.total_articles} articles</span>
                              <span>{job.successful_articles} succeeded</span>
                              {job.failed_articles > 0 && (
                                <span className="text-red-600">{job.failed_articles} failed</span>
                              )}
                              <span>{new Date(job.created_at).toLocaleDateString()}</span>
                              {isAdmin && job.created_by_email && (
                                <span className="text-blue-600">{job.created_by_email}</span>
                              )}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" className="shrink-0">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Results List */}
                <div>
                  <div className="flex items-center justify-between mb-3 gap-3">
                    <h4 className="text-sm font-medium flex items-center gap-2 shrink-0">
                      <FileText className="h-4 w-4" />
                      Enhanced Articles
                    </h4>
                    <div className="flex items-center gap-2">
                      {/* Display Number Filter */}
                      <Input
                        placeholder="Search by KB number..."
                        value={enhancementsDisplayNumberFilter}
                        onChange={(e) => setEnhancementsDisplayNumberFilter(e.target.value)}
                        className="h-7 w-40 text-xs"
                        onClick={(e) => e.stopPropagation()}
                      />
                      {/* Toggle for All vs My Enhancements */}
                      <div className="flex rounded-lg border overflow-hidden">
                        <button
                          className={cn(
                            'px-3 py-1 text-xs font-medium flex items-center gap-1.5 transition-colors',
                            !showMyEnhancementsOnly
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-background hover:bg-muted'
                          )}
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowMyEnhancementsOnly(false)
                          }}
                        >
                          <Users className="h-3 w-3" />
                          All
                        </button>
                        <button
                          className={cn(
                            'px-3 py-1 text-xs font-medium flex items-center gap-1.5 transition-colors',
                            showMyEnhancementsOnly
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-background hover:bg-muted'
                          )}
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowMyEnhancementsOnly(true)
                          }}
                        >
                          <User className="h-3 w-3" />
                          My Enhancements
                        </button>
                      </div>
                    </div>
                  </div>
                  {historyLoading ? (
                    <div className="space-y-2">
                      {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                      ))}
                    </div>
                  ) : enhancementHistory?.results.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4 text-sm">
                      {showMyEnhancementsOnly ? 'No articles enhanced by you yet' : 'No enhanced articles yet'}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {enhancementHistory?.results
                        .filter((result) =>
                          !enhancementsDisplayNumberFilter ||
                          result.display_number.toLowerCase().includes(enhancementsDisplayNumberFilter.toLowerCase())
                        )
                        .map((result) => (
                        <div
                          key={result.display_number}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={async () => {
                            const detail = await enhancementsApi.getResult(result.display_number)
                            setEnhancementResultPreview(detail.result)
                          }}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs text-muted-foreground">
                                {result.display_number}
                              </span>
                              <Badge
                                variant="outline"
                                className={cn(
                                  'text-xs',
                                  result.enhancement_status === 'completed' && 'bg-green-100 text-green-800',
                                  result.enhancement_status === 'failed' && 'bg-red-100 text-red-800',
                                )}
                              >
                                {result.enhancement_status}
                              </Badge>
                              {result.submitted_to_servicenow && (
                                <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800">
                                  <Upload className="h-3 w-3 mr-1" />
                                  Submitted
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium truncate">{result.article_title}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(result.enhancement_date).toLocaleDateString()} - {result.knowledge_base}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>

      {/* Preview Modal with Issues Drawer */}
      <ArticlePreviewModal
        article={previewArticle}
        open={!!previewArticle}
        onClose={() => setPreviewArticle(null)}
        onEnhance={handleQuickEnhance}
        enhancementStatus={previewArticle ? getArticleStatus(previewArticle.display_number) : null}
        onViewEnhancement={handleViewEnhancement}
      />

      {/* Enhancement Result Side-by-Side Preview */}
      <EnhancementResultPreview
        result={enhancementResultPreview}
        open={!!enhancementResultPreview}
        onClose={() => setEnhancementResultPreview(null)}
      />

      {/* Job Results Modal */}
      <Dialog open={!!viewingJobResults} onOpenChange={() => setViewingJobResults(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Job Results</DialogTitle>
            <DialogDescription>
              Articles enhanced in this job
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {jobResultsLoading ? (
              <div className="space-y-2 p-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : jobResultsData?.results.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4" />
                <p>No results found for this job</p>
                <p className="text-sm mt-2">Results may not be linked to this job yet</p>
              </div>
            ) : (
              <div className="space-y-2 p-4">
                {jobResultsData?.results.map((result) => (
                  <div
                    key={result.display_number}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={async () => {
                      const detail = await enhancementsApi.getResult(result.display_number)
                      setViewingJobResults(null) // Close the job results modal first
                      setEnhancementResultPreview(detail.result)
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs text-muted-foreground">
                          {result.display_number}
                        </span>
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-xs',
                            result.enhancement_status === 'completed' && 'bg-green-100 text-green-800',
                            result.enhancement_status === 'failed' && 'bg-red-100 text-red-800',
                          )}
                        >
                          {result.enhancement_status}
                        </Badge>
                        {result.submitted_to_servicenow && (
                          <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800">
                            <Upload className="h-3 w-3 mr-1" />
                            Submitted
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-medium truncate">{result.article_title}</p>
                      <p className="text-xs text-muted-foreground">
                        {result.knowledge_base}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
